import React, { useEffect, useRef, useState } from "react";
import { Box, useTheme } from "@mui/material";
import { useGridApiRef } from "@mui/x-data-grid";
import {
  setCollectionId,
  setCollectionSourceId,
  setIsCitationAvailable,
  setIsSidebarOpen,
  setIsSummaryAvailable,
  setLevel,
  setReadingLevelAvailable,
  setSelectedBookmarkOption,
  setSummaryInfoData,
} from "context/features/dataSlice";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { writeStorage, useLocalStorage } from "@rehooks/local-storage";
import ColumnsSection from "./ColumnSection";
import CollectionTableComponent from "features/SourceSearch/components/collection-table-component";
import { useColumns } from "features/SourceSearch/components/generate-columns";
import { publicCollectionColumns, publicCollectiondefaultVisibilityModel } from "../data/columns";
import { isDatabaseSourceUrl } from "utils/database-source.util";

const PublicCollectionTable = ({ rows, loading = false, filter }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const apiRef = useGridApiRef();
  const { id } = useParams();

  const { user } = useSelector((state) => state.auth);

  const noRowsMessage = filter
    ? "No bookmarks found matching your search."
    : "It looks like there are no bookmarks in this collection yet!";

  const [visibilityModel, setVisibilityModel] = useLocalStorage(
    `sooth-api-public-table-columnvisibility-${user.userId}`,
    publicCollectiondefaultVisibilityModel
  );

  const [persistedWidths, setPersistedWidths] = useLocalStorage(
    `sooth-api-public-table-columnWidths-${user.userId}`,
    {}
  );

  const [commonWidth, setCommonWidth] = useState(null);

  const columnWidthsRef = useRef(persistedWidths);
  const containerRef = useRef(null);

  const computeCommonWidth = () => {
    const element = containerRef.current;
    let width = element ? element.clientWidth : 0;

    const visibleFields = Object.keys(visibilityModel).filter(
      (field) => visibilityModel[field]
    );
    const unsetCount = visibleFields.length;
    const newCommonWidth = unsetCount > 0 ? Math.floor(width / unsetCount) : 0;
    setCommonWidth(newCommonWidth);
  };

  const debouncedUpdateColumnWidths = () => {
    writeStorage(
      `sooth-api-public-table-columnWidths-${user.userId}`,
      columnWidthsRef.current
    );
    setPersistedWidths(columnWidthsRef.current);
    computeCommonWidth();
  };

  const handleColumnResize = (params) => {
    columnWidthsRef.current = {
      ...columnWidthsRef.current,
      [params.colDef.field]: params.width,
    };
    debouncedUpdateColumnWidths();
  };

  useEffect(() => {
    if (Object.keys(persistedWidths).length === 0) {
      setCommonWidth(null);
    }
  }, [persistedWidths]);

  useEffect(() => {
    if (!visibilityModel || Object.keys(visibilityModel).length === 0) {
      setVisibilityModel(publicCollectiondefaultVisibilityModel);
      writeStorage(
        `sooth-api-public-table-columnvisibility-${user.userId}`,
        publicCollectiondefaultVisibilityModel
      );
      apiRef.current.setColumnVisibilityModel(publicCollectiondefaultVisibilityModel);
    } else {
      apiRef.current.setColumnVisibilityModel(visibilityModel);
    }
  }, []);

  const handleSidebarOpen = (item, option) => {
    const isDatabaseSearch = isDatabaseSourceUrl(item.sourceUrl_chr);
    dispatch(setCollectionSourceId(item.collectionSourceID_ids));
    dispatch(setLevel(""));
    dispatch(
      setSelectedBookmarkOption(
        isDatabaseSearch && option === "readinglevel" ? "notes" : option
      )
    );
    dispatch(setReadingLevelAvailable(item.isReadingLevelAvailable_ysn));
    dispatch(
      setSummaryInfoData({
        publisher: item.sourceName_chr,
        title: item.title_chr,
        published_date: item.sourcePublishedDateTime_dtm,
        url: item.sourceUrl_chr,
        utc_date_published: item.sourcePublishedDateTime_dtm,
        isDatabaseSearch,
      })
    );
    dispatch(setCollectionId(item.collectionID_lng));
    dispatch(setIsCitationAvailable(item.isCitationAvailable_ysn));
    dispatch(setIsSummaryAvailable(item.isSummaryAvailable_ysn));
    dispatch(setIsSidebarOpen(true));
  };

  const handleVisibilityChange = (newVisibilityModel) => {
    const updatedVisibility = {
      ...visibilityModel,
      ...newVisibilityModel,
    };
    setVisibilityModel(updatedVisibility);
    writeStorage(
      `sooth-api-public-table-columnvisibility-${user.userId}`,
      updatedVisibility
    );
  };

  const columns = useColumns({
    fields: publicCollectionColumns,
    persistedWidths,
    commonWidth,
    theme,
    handlers: { handleSidebarOpen }
  });

  const scrollToLeft = () => {
    const virtualScroller = document.querySelector(
      ".MuiDataGrid-virtualScroller"
    );
    if (virtualScroller) {
      virtualScroller.scrollLeft = 0;
    }
  };

  const clearWidthModel = () => {
    scrollToLeft();
    writeStorage(`sooth-api-public-table-columnWidths-${user.userId}`, {});
    setCommonWidth(null);
    setPersistedWidths({});
    columnWidthsRef.current = {};
  };

  return (
    <Box ref={containerRef} sx={{ width: "100%" }}>
      <ColumnsSection
        visibilityModel={visibilityModel}
        setVisibilityModel={setVisibilityModel}
        columns={columns}
        label={`sooth-api-public-table-columnvisibility-${user.userId}`}
        clearWidthModel={clearWidthModel}
        widthModal={persistedWidths}
      />
      <CollectionTableComponent
        apiRef={apiRef}
        rows={rows}
        columns={columns}
        handleColumnResize={handleColumnResize}
        visibilityModel={visibilityModel}
        handleVisibilityChange={handleVisibilityChange}
        getRowId={(row) => row.publicCollectionSourceID_chr}
        getRowClassName={({ row }) =>
          row.isMisinformationSource_ysn ? "sooth-misinformation-row" : ""
        }
        showCustomEmptyMessage={true}
        loading={loading}
        noRowsMessage={noRowsMessage}
      />
    </Box>
  );
};

export default PublicCollectionTable;
